import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Table, Input } from "reactstrap";
import moment from "moment";

const entero = (n) => new Intl.NumberFormat("en-US").format(n || 0);

// Ventas de Mercado Libre que no entraron al sistema: las que la integracion vio
// y no pudo registrar, y las que se encuentran al comparar contra las ordenes de Meli
function VentasPendientesMeli({ URL, headers, cuentas, habilitado, error, alCambiar }) {
  const [pendientes, setPendientes] = useState(null);
  const [cuenta, setCuenta] = useState("");
  const [desde, setDesde] = useState(moment().subtract(1, "days").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));
  const [comparacion, setComparacion] = useState(null);
  const [trabajando, setTrabajando] = useState("");

  const carga = () =>
    axios
      .get(`${URL}VentasPendientes${cuenta ? `?cuenta=${cuenta}` : ""}`, { headers })
      .then((r) => setPendientes(r.data))
      .catch((e) => error(e, "No se pudo cargar"));

  useEffect(() => {
    carga();
    setComparacion(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuenta]);

  function compara() {
    setTrabajando("Leyendo las ordenes de Mercado Libre...");
    const p = new URLSearchParams({ desde, hasta });
    if (cuenta) p.set("cuenta", cuenta);
    axios
      .get(`${URL}OrdenesFaltantes?${p.toString()}`, { headers })
      .then((r) => setComparacion(r.data))
      .catch((e) => error(e, "No se pudo comparar"))
      .finally(() => setTrabajando(""));
  }

  // Pasa las ordenes por el corte, cada una con la fecha de su venta
  async function registra(filas) {
    const porCuenta = {};
    filas.forEach((f) => {
      porCuenta[f.idCuenta] = porCuenta[f.idCuenta] || new Set();
      porCuenta[f.idCuenta].add(f.orderId);
    });
    const ordenes = Object.values(porCuenta).reduce((t, s) => t + s.size, 0);
    const ok = await Swal.fire({
      title: `Registrar ${ordenes} orden(es)`,
      text: "Full entra a un surtido y Drop a un pedido pendiente de surtir, con la fecha de su venta. Primero se revisan en Mercado Libre.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Registrar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setTrabajando("Registrando...");
    const total = { nuevas: 0, sinCasar: 0, canceladas: 0, surtidos: [], pedidos: [] };
    try {
      for (const [id, set] of Object.entries(porCuenta)) {
        const lista = [...set];
        for (let i = 0; i < lista.length; i += 200) {
          const r = await axios.post(`${URL}RegistraOrdenes/${id}`, { ordenes: lista.slice(i, i + 200) }, { headers });
          total.nuevas += r.data.nuevas;
          total.sinCasar += r.data.sinCasar;
          total.canceladas += r.data.canceladas;
          total.surtidos.push(...r.data.surtidos);
          total.pedidos.push(...r.data.pedidos);
        }
      }
      Swal.fire({
        icon: total.sinCasar ? "warning" : "success",
        title: `${total.nuevas} venta(s) registradas`,
        html: `${total.surtidos.length ? `Surtidos: ${total.surtidos.join(", ")}<br>` : ""}${total.pedidos.length ? `Pedidos Drop: ${total.pedidos.join(", ")}<br>` : ""}${
          total.sinCasar ? `${total.sinCasar} siguen sin articulo en Cuory<br>` : ""
        }${total.canceladas ? `${total.canceladas} ya estaban canceladas en Meli` : ""}`,
      });
    } catch (e) {
      error(e, "No se pudo registrar");
    }
    setTrabajando("");
    setComparacion(null);
    carga();
    if (alCambiar) alCambiar();
  }

  const listas = (pendientes || []).filter((f) => f.reintentable && f.articuloCuory);
  const faltantes = comparacion ? comparacion.filas : [];

  return (
    <>
      <div className="intg-barra">
        <Input bsSize="sm" type="select" value={cuenta} onChange={(e) => setCuenta(e.target.value)} style={{ width: 180 }}>
          <option value="">Todas las cuentas</option>
          {cuentas
            .filter((c) => c.plataforma === "Mercado Libre")
            .map((c) => (
              <option key={c._id} value={c._id}>
                {c.nombre}
              </option>
            ))}
        </Input>
        <Button size="sm" color="success" disabled={!listas.length || !habilitado || !!trabajando} onClick={() => registra(listas)}>
          <i className="fas fa-check"></i> Registrar las que ya tienen articulo ({entero(listas.length)})
        </Button>
        {trabajando ? <span style={{ fontSize: 12, color: "#64748b" }}>{trabajando}</span> : null}
      </div>
      <h6 style={{ fontWeight: 600 }}>Vistas por la integracion y sin registrar</h6>
      {!pendientes ? (
        <div className="intg-vacio">Cargando...</div>
      ) : !pendientes.length ? (
        <div className="intg-vacio">
          <i className="fas fa-check-circle"></i>
          Todas las ventas que vio la integracion estan registradas.
        </div>
      ) : (
        <Table size="sm" striped borderless className="table-responsive-xl">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cuenta</th>
              <th>Orden</th>
              <th>SKU</th>
              <th>Titulo</th>
              <th style={{ textAlign: "right" }}>Pzs</th>
              <th>Tipo</th>
              <th>Motivo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pendientes.map((f) => (
              <tr key={f._id}>
                <td style={{ whiteSpace: "nowrap" }}>{f.fecha}</td>
                <td>{f.cuenta}</td>
                <td style={{ fontSize: 12 }}>{f.orderId}</td>
                <td>
                  <b>{f.sku || "Sin SKU"}</b>
                </td>
                <td style={{ fontSize: 12, maxWidth: 280 }}>{f.titulo}</td>
                <td align="right">{f.cantidad}</td>
                <td>
                  <span className={`intg-etq ${f.logistica === "fulfillment" ? "full" : "drop"}`}>{f.logistica === "fulfillment" ? "Full" : "Drop"}</span>
                </td>
                <td style={{ fontSize: 12 }}>{f.articuloCuory ? `Ya existe: ${f.articuloCuory}` : f.motivo}</td>
                <td>
                  {f.reintentable && f.articuloCuory ? (
                    <Button size="sm" color="success" disabled={!habilitado || !!trabajando} onClick={() => registra([f])}>
                      Registrar
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="intg-seccion">
        <h6 style={{ fontWeight: 600 }}>Comparar contra las ordenes de Mercado Libre</h6>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
          Busca ventas pagadas que existen en Meli y que Cuory no tiene. Solo lee; maximo 7 dias. Cada dia tarda cerca de medio minuto.
        </div>
        <div className="intg-barra">
          <Input bsSize="sm" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 150 }} />
          <Input bsSize="sm" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 150 }} />
          <Button size="sm" color="primary" disabled={!habilitado || !!trabajando} title={habilitado ? "Puede tardar unos minutos: revisa orden por orden" : "Modo preparativo: no se jala nada"} onClick={compara}>
            <i className="fas fa-search"></i> Comparar
          </Button>
          {faltantes.some((f) => f.articulo) ? (
            <Button size="sm" color="success" disabled={!!trabajando} onClick={() => registra(faltantes.filter((f) => f.articulo))}>
              Registrar las que tienen articulo ({entero(faltantes.filter((f) => f.articulo).length)})
            </Button>
          ) : null}
        </div>
        {comparacion ? (
          <>
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 8 }}>
              {entero(comparacion.resumen.revisadas)} ventas revisadas · <b>{entero(comparacion.resumen.faltantes)} sin capturar</b> ·{" "}
              {entero(comparacion.resumen.enCamino)} entran en el siguiente corte
            </div>
            {!faltantes.length ? (
              <div className="intg-vacio">
                <i className="fas fa-check-circle"></i>
                Todas las ventas de esas fechas estan en Cuory.
              </div>
            ) : (
              <Table size="sm" striped borderless className="table-responsive-xl">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cuenta</th>
                    <th>Orden</th>
                    <th>SKU</th>
                    <th>Titulo</th>
                    <th style={{ textAlign: "right" }}>Pzs</th>
                    <th style={{ textAlign: "right" }}>Precio</th>
                    <th>Articulo Cuory</th>
                  </tr>
                </thead>
                <tbody>
                  {faltantes.map((f, i) => (
                    <tr key={`${f.orderId}-${i}`}>
                      <td style={{ whiteSpace: "nowrap" }}>{f.fecha}</td>
                      <td>{f.cuenta}</td>
                      <td style={{ fontSize: 12 }}>{f.orderId}</td>
                      <td>
                        <b>{f.sku || "Sin SKU"}</b>
                      </td>
                      <td style={{ fontSize: 12, maxWidth: 280 }}>{f.titulo}</td>
                      <td align="right">{f.cantidad}</td>
                      <td align="right">${entero(f.precioBruto)}</td>
                      <td style={f.articulo ? undefined : { color: "#b91c1c" }}>{f.articulo || "Falta dar de alta"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}

export default VentasPendientesMeli;
